/**
 * QuotationApprovalBanner — surfaces the approval-matrix state for
 * the currently-selected quotation. Three modes:
 *   • Auto-approved (low value) — quietly hidden.
 *   • Required but not yet requested — shows "Request Approval" button.
 *   • Pending / approved / rejected — shows state badge.
 * Server is the source of truth; the UI just renders /approval-matrix
 * + the quotation's approval_matrix_state fields.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ShieldCheck, ShieldAlert, ShieldX, Loader2, Send } from 'lucide-react';

export default function QuotationApprovalBanner({ quotation, onApprovalRequested, onApprovalDecided }) {
  const [tier, setTier]       = useState(null);    // matched tier for THIS quotation amount
  const [matrix, setMatrix]   = useState(null);
  const [requesting, setRequesting] = useState(false);

  // Fetch matrix once + match the quotation amount to a tier locally.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/approval-matrix');
        if (cancelled) return;
        setMatrix(data);
        const total = Number(quotation?.total || 0);
        const matched = (data.tiers || []).find((t) =>
          total >= Number(t.min || 0)
          && (t.max === null || t.max === undefined || total < Number(t.max))
        );
        setTier(matched || null);
      } catch (_) { /* fail-soft — banner just hides */ }
    })();
    return () => { cancelled = true; };
  }, [quotation?.id, quotation?.total]);

  if (!tier) return null;
  // Auto-approved tier — no banner noise.
  if (!tier.approver_role) {
    if (quotation?.approval_matrix_state === 'auto_approved') {
      return (
        <div
          data-testid="quotation-approval-banner-auto"
          className="mb-3 px-3 py-2 rounded-md text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1.5"
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          Auto-approved by matrix · {tier.label}
        </div>
      );
    }
    return null;
  }

  const state = quotation?.approval_matrix_state;
  const tierLabel = tier.label;
  const roleLabel = tier.approver_role;

  const requestApproval = async () => {
    setRequesting(true);
    try {
      const { data } = await axiosInstance.post(
        `/quotations/${quotation.id}/request-approval`
      );
      if (data.required === false) {
        toast.success('Auto-approved by matrix');
      } else if (data.already_open) {
        toast.info('An approval request is already pending');
      } else {
        toast.success(`Approval requested · routed to ${roleLabel}`);
      }
      onApprovalRequested?.(data);
    } catch (err) {
      toast.error('Failed to request approval');
    } finally {
      setRequesting(false);
    }
  };

  // No state yet → render the "Request approval" CTA
  if (!state || state === 'auto_approved') {
    return (
      <div
        data-testid="quotation-approval-banner-required"
        className="mb-3 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 text-sm">
          <ShieldAlert className="w-4 h-4 text-amber-600" />
          <div>
            <div className="font-semibold text-amber-900">
              Approval required: {tierLabel}
            </div>
            <div className="text-xs text-amber-700">
              Routes to <strong>{roleLabel}</strong> before convert-to-invoice.
            </div>
          </div>
        </div>
        <button
          data-testid="request-approval-button"
          onClick={requestApproval}
          disabled={requesting}
          className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-md inline-flex items-center gap-1.5"
        >
          {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Request Approval
        </button>
      </div>
    );
  }

  if (state === 'pending') {
    return (
      <div
        data-testid="quotation-approval-banner-pending"
        className="mb-3 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center gap-2 text-sm"
      >
        <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
        <div>
          <div className="font-semibold text-amber-900">
            Awaiting {roleLabel} approval · {tierLabel}
          </div>
          <div className="text-xs text-amber-700">
            Convert-to-invoice is blocked until this is cleared.
          </div>
        </div>
      </div>
    );
  }

  if (state === 'approved') {
    return (
      <div
        data-testid="quotation-approval-banner-approved"
        className="mb-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50 flex items-center gap-2 text-sm"
      >
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        <span className="font-semibold text-emerald-900">
          Approved · {tierLabel}
        </span>
      </div>
    );
  }

  if (state === 'rejected') {
    return (
      <div
        data-testid="quotation-approval-banner-rejected"
        className="mb-3 p-3 rounded-lg border border-red-200 bg-red-50 flex items-center gap-2 text-sm"
      >
        <ShieldX className="w-4 h-4 text-red-600" />
        <div>
          <div className="font-semibold text-red-900">Rejected · {tierLabel}</div>
          <div className="text-xs text-red-700">Convert is blocked. Revise the quotation and submit a new request.</div>
        </div>
      </div>
    );
  }

  return null;
}
